import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  listProjects,
  getProject,
  createProject,
  duplicateProject,
  updateProject,
  deleteProject,
  getPipelineStatus,
  getProjectCurrentLoad,
} from "@/api/projects";
import {
  getAgentSchema,
  getPreparedInputs,
  getExecutions,
  getExecution,
  getChatHistory,
  getProjectCosts,
  type GetProjectCostsOptions,
  getProjectRuns,
  type GetProjectRunsOptions,
  getProjectRunStats,
} from "@/api/agents";
import { getArtifactCount, listArtifacts } from "@/api/artifacts";
import {
  getProjectMembers,
  getAssignableUsers,
  addProjectMember,
  revokeProjectMember,
} from "@/api/project-members";
import {
  getAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  kickAdminUser,
  type CreateAdminUserPayload,
  type UpdateAdminUserPayload,
} from "@/api/admin-users";
import {
  getWorkspaceSettings,
  updateWorkspaceSettings,
  type UpdateWorkspaceSettingsPayload,
} from "@/api/admin-workspace";
import { register } from "@/api/auth";
import {
  listReferenceDataSources,
  listReferenceDataRecords,
  getReferenceDataSchema,
  getReferenceDataHistory,
  listReferenceDataVersions,
  uploadReferenceDataFile,
  ingestReferenceData,
  getReferenceDataIngestStatus,
  promoteReferenceData,
  rollbackReferenceData,
  listRecordVersions,
  rollbackReferenceDataRecord,
  stageReferenceDataEdits,
  listReferenceDataAgents,
  listConfigPackages,
  getConfigPackage,
  uploadConfigTypeFile,
  ingestConfigType,
  promoteConfigPackage,
  rollbackConfigPackage,
} from "@/api/reference-data";
import type { ReferenceDataEditOperation } from "@/types/reference-data";
import {
  getTargetConnection,
  updateTargetConnection,
  updateTargetCredentials,
  testTargetConnection,
  startAdtLogon,
  getAdtStatus,
  disconnectAdt,
  keepAdtAlive,
} from "@/api/target-connection";
import { callbackMode } from "@/lib/code-companion-session";
import { getGrowSignals, setGrowSignal, verifyGrowSignal } from "@/api/grow-signals";
import type {
  CreateProjectRequest,
  ProjectDetail,
  UpdateProjectRequest,
  UpdateTargetConnectionRequest,
  TargetCredentialsRequest,
  ClientRole,
  RegisterRequest,
} from "@/types/api";

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export function useProjects() {
  return useQuery({
    queryKey: ["projects"],
    queryFn: listProjects,
  });
}

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: ["project", projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
  });
}

/**
 * A carga vigente do ex-kit e a que está chegando (#1785): o que o projeto tem da origem, no
 * cartão da Visão geral e na página Conexões. Banco do cliente fora do ar volta 503, e a tela
 * diz que não deu para ler; sem retry, para o cartão não ficar girando.
 */
export function useProjectCurrentLoad(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projectCurrentLoad", projectId],
    queryFn: () => getProjectCurrentLoad(projectId!),
    enabled: !!projectId,
    retry: false,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectRequest) => createProject(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projects"] }),
  });
}

export function useDuplicateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, name }: { projectId: string; name?: string }) =>
      duplicateProject(projectId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projects"] }),
  });
}

export function useUpdateProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateProjectRequest) => updateProject(projectId, data),
    onSuccess: (updated) => {
      /**
       * A resposta do `PATCH` entra no cache, e não só invalida.
       *
       * Invalidar sozinho joga fora o corpo que o backend acabou de devolver, e
       * quem salva por formulário paga: a tela limpa o rascunho assim que o
       * `mutateAsync` resolve, os campos voltam a seguir o cache, e o cache
       * ainda tem o valor **antigo** até o refetch chegar. O campo pisca de
       * volta ao nome anterior logo depois do toast de sucesso — e se o refetch
       * falhar, fica assim.
       *
       * ⚠️ **Merge, não substituição.** O `PATCH` devolve `ProjectResponse`; esta
       * chave guarda um `ProjectDetail`, que é o mesmo objeto **mais**
       * `latest_executions`. Um `setQueryData(key, updated)` cru apagaria esse
       * campo, e a Overview do projeto lê a mesma chave.
       *
       * ⚠️ **`old ? … : old`, e não semear.** `Projects.tsx` chama este hook uma
       * vez por linha da lista (Arquivar/Restaurar), onde `["project", id]` quase
       * nunca está carregado — a lista usa `["projects"]`. Criar a entrada ali
       * deixaria um detalhe parcial, sem `latest_executions`, que a Overview
       * leria como se fosse completo.
       *
       * O `invalidateQueries` continua logo abaixo: isto mata o flash imediato,
       * o refetch traz o que a resposta não carrega.
       */
      qc.setQueryData<ProjectDetail>(["project", projectId], (old) =>
        old ? { ...old, ...updated } : old,
      );
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => deleteProject(projectId),
    onSuccess: (_, projectId) => {
      qc.removeQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Pipeline
// ---------------------------------------------------------------------------

export function usePipelineStatus(projectId: string | undefined) {
  return useQuery({
    queryKey: ["pipeline", projectId],
    queryFn: () => getPipelineStatus(projectId!),
    enabled: !!projectId,
    refetchInterval: 10_000,
  });
}

// ---------------------------------------------------------------------------
// Agent Definitions & Execution
// ---------------------------------------------------------------------------

export function useAgentSchema(
  projectId: string | undefined,
  agentId: string | undefined,
) {
  return useQuery({
    queryKey: ["agentSchema", projectId, agentId],
    queryFn: () => getAgentSchema(projectId!, agentId!),
    enabled: !!projectId && !!agentId,
  });
}

export function usePreparedInputs(
  projectId: string | undefined,
  agentId: string | undefined,
) {
  return useQuery({
    queryKey: ["preparedInputs", projectId, agentId],
    queryFn: () => getPreparedInputs(projectId!, agentId!),
    enabled: !!projectId && !!agentId,
  });
}

export function useExecutions(
  projectId: string | undefined,
  agentId: string | undefined,
  options?: { slim?: boolean },
) {
  // S-018: slim opt-in. Quando true, response omite output_summary e
  // input_config — drástica redução de payload pra listas em UI. Caller
  // deve usar `useExecution(executionId)` pra fetchar o full state quando
  // precisar do output_summary.
  const slim = options?.slim ?? false;
  return useQuery({
    queryKey: ["executions", projectId, agentId, { slim }],
    queryFn: () => getExecutions(projectId!, agentId!, { slim }),
    enabled: !!projectId && !!agentId,
  });
}

export function useExecution(
  projectId: string | undefined,
  agentId: string | undefined,
  executionId: string | undefined | null,
) {
  return useQuery({
    queryKey: ["execution", projectId, agentId, executionId],
    queryFn: () => getExecution(projectId!, agentId!, executionId!),
    enabled: !!projectId && !!agentId && !!executionId,
  });
}

// ---------------------------------------------------------------------------
// Artifacts
// ---------------------------------------------------------------------------

export function useArtifacts(projectId: string | undefined) {
  return useQuery({
    queryKey: ["artifacts", projectId],
    queryFn: () => listArtifacts(projectId!),
    enabled: !!projectId,
    // Cada artifact carrega o `artifact_data` inteiro — com BOM gerada isso é
    // megabytes. staleTime 0 (o default) refazia essa busca a cada navegação
    // que remonta a página; o dado não muda tão rápido a ponto de precisar disso.
    staleTime: 60_000,
  });
}

/** Só a contagem — o badge do sidebar não precisa do `artifact_data` de cada
 * artifact (ver `useArtifacts`). Endpoint dedicado (`/artifacts/count`) evita
 * baixar o payload inteiro em toda rota do projeto. */
export function useArtifactCount(projectId: string | undefined) {
  return useQuery({
    queryKey: ["artifacts", projectId, "count"],
    queryFn: () => getArtifactCount(projectId!),
    enabled: !!projectId,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Target connection (GROW) — S-043
// ---------------------------------------------------------------------------

/** O link do botão View das SSCUIs vem da URL do tenant Configuration: ao salvar ou
 * testar o tenant, árvore e folhas precisam ser relidas para ganhar (ou perder) o link. */
function invalidateSscuiLaunchLinks(qc: QueryClient, projectId: string) {
  qc.invalidateQueries({ queryKey: ["config-server", "sscui-tree", projectId] });
  qc.invalidateQueries({ queryKey: ["sscui-tree-activities", projectId] });
}

export function useTargetConnection(projectId: string | undefined, clientRole: ClientRole) {
  return useQuery({
    queryKey: ["targetConnection", projectId, clientRole],
    queryFn: () => getTargetConnection(projectId!, clientRole),
    enabled: !!projectId,
  });
}

export function useUpdateTargetConnection(projectId: string, clientRole: ClientRole) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateTargetConnectionRequest) =>
      updateTargetConnection(projectId, clientRole, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["targetConnection", projectId] });
      // Configurar o target de desenvolvimento destrava Architect/CC no gating — refaz o
      // pipeline. O tenant Configuration não participa do gating.
      if (clientRole === "development") {
        qc.invalidateQueries({ queryKey: ["pipeline", projectId] });
      } else {
        invalidateSscuiLaunchLinks(qc, projectId);
      }
    },
  });
}

export function useUpdateTargetCredentials(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TargetCredentialsRequest) =>
      updateTargetCredentials(projectId, data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["targetConnection", projectId] }),
  });
}

export function useTestTargetConnection(projectId: string, clientRole: ClientRole) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => testTargetConnection(projectId, clientRole),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["targetConnection", projectId] });
      if (clientRole === "configuration") invalidateSscuiLaunchLinks(qc, projectId);
    },
  });
}

// --- Sessão ADT do modo B, por usuário (S-048) ------------------------------

/** Status da sessão ADT do dev logado; refaz em intervalo pra refletir expiração e
 * ao focar a janela (volta da aba do SSO → mostra "conectado" na hora). */
export function useAdtStatus(projectId: string | undefined) {
  return useQuery({
    queryKey: ["adtStatus", projectId],
    queryFn: () => getAdtStatus(projectId!),
    enabled: !!projectId,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 0, // sobrepõe o staleTime global (30s) pro focus revalidar na hora
  });
}

export function useStartAdtLogon(projectId: string) {
  // O modo decide-se aqui, e não em cada tela: dois componentes disparam o logon
  // (AdtSessionPanel e ConductorStrip), e deixar a escolha em cada um garantiria que
  // um dia um deles ficasse para trás. Ver S-070.
  return useMutation({
    mutationFn: () => startAdtLogon(projectId, callbackMode(window.location.hostname)),
  });
}

export function useDisconnectAdt(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectAdt(projectId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["adtStatus", projectId] }),
  });
}

export function useAdtKeepalive(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => keepAdtAlive(projectId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["adtStatus", projectId] }),
  });
}

export function useGrowSignals(projectId: string | undefined) {
  return useQuery({
    queryKey: ["growSignals", projectId],
    queryFn: () => getGrowSignals(projectId!),
    enabled: !!projectId,
  });
}

export function useSetGrowSignal(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scenarioId,
      status,
      reason,
    }: {
      scenarioId: string;
      status: "pending" | "activated" | "unavailable";
      reason?: string;
    }) => setGrowSignal(projectId, scenarioId, { status, reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["growSignals", projectId] }),
  });
}

export function useVerifyGrowSignal(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, servicePath }: { scenarioId: string; servicePath: string }) =>
      verifyGrowSignal(projectId, scenarioId, servicePath),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["growSignals", projectId] }),
  });
}

// ---------------------------------------------------------------------------
// Cost Summary
// ---------------------------------------------------------------------------

export function useProjectCosts(
  projectId: string | undefined,
  options?: GetProjectCostsOptions & {
    /** Segura a consulta enquanto quem chama ainda não sabe se vai usá-la
     * (ex.: o recorte do mês só interessa se o tenant tem `budget_cap_usd`).
     * Default `true` — quem não passa nada continua como antes. */
    enabled?: boolean;
  },
) {
  // `enabled` é opção de query, não de requisição: fica fora do que vai para
  // o cliente HTTP.
  const { enabled = true, ...request } = options ?? {};
  return useQuery({
    // `includeDaily` entra na chave: sem ele, a resposta enxuta da Overview
    // (sem `by_day`) seria servida do cache para a tela de custo, que
    // desenharia empty state de histórico para um projeto que tem histórico.
    queryKey: [
      "projectCosts",
      projectId,
      request.dateFrom ?? null,
      request.includeDaily ?? false,
    ],
    queryFn: () => getProjectCosts(projectId!, request),
    enabled: !!projectId && enabled,
  });
}

// ---------------------------------------------------------------------------
// Admin — Users (Team view, PR D)
// ---------------------------------------------------------------------------
//
// O portao nao e uniforme, e a diferenca e o desenho da tela: **ler o roster
// exige `manager`** (`require_min_role(roles.MANAGER)`) e **escrever continua
// `get_current_admin`** — criar, mudar papel, desativar e derrubar sessao.
//
// Por isso o `/admin/team` aparece no menu de um manager (`app-nav.ts`): antes
// da descida a tela caia no estado vazio e escrevia "No team members yet",
// mentindo que o workspace nao tem ninguem. O que ela esconde do manager sao os
// controles de escrita, um por um, e nao a tela.
//
// Todas as mutations invalidam `["admin", "users"]` para o roster acompanhar.

export function useAdminUsers(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: getAdminUsers,
    // Roster doesn't change at poll speed; 60s is fine and cheaper than
    // the 30s cadence used for execution-driven views.
    refetchInterval: 60_000,
    // Ler o roster exige `manager`; **escrever** nele continua `admin`. O
    // `enabled` existe para quem monta a query de um lugar que alcança menos que
    // isso, e não quer disparar um 403 de graça a cada montagem.
    enabled: options.enabled ?? true,
  });
}

export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAdminUserPayload) => createAdminUser(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

/**
 * Convite pelo diretório — o caminho do `manager`.
 *
 * `POST /api/auth/register` e não `POST /api/admin/users`: são rotas diferentes
 * com fronteiras diferentes, e **a diferença é o ponto**. Esta exige `manager`,
 * nunca aceita senha (400) e só concede `consultant` ou `user`; aquela exige
 * admin, é a única que cria conta com senha e a única que concede papel
 * privilegiado. Quem convida não escolhe a política de autenticação do workspace.
 */
export function useInviteFromDirectory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RegisterRequest) => register(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useUpdateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: string;
      payload: UpdateAdminUserPayload;
    }) => updateAdminUser(userId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useDeleteAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => deleteAdminUser(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useKickAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => kickAdminUser(userId),
    // Kick doesn't change roster shape, just bumps user_generation —
    // still invalidate so any UI showing "last active" picks up the
    // forced session-revoke timestamp if we add one later.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

// ---------------------------------------------------------------------------
// Admin — Workspace settings + password change (Settings view, PR E)
// ---------------------------------------------------------------------------

export function useWorkspaceSettings() {
  return useQuery({
    queryKey: ["admin", "workspace-settings"],
    queryFn: getWorkspaceSettings,
    // Settings rarely change; refetch on focus is enough.
    staleTime: 5 * 60_000,
  });
}

export function useUpdateWorkspaceSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateWorkspaceSettingsPayload) =>
      updateWorkspaceSettings(payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "workspace-settings"] }),
  });
}

// ---------------------------------------------------------------------------
// Admin — Reference Data hub.
// Os 13 handlers exigem `consultant`, leitura e escrita (S-073): dado de
// referencia e insumo de trabalho de quem executa o projeto. As tabelas `ref_*`
// sao globais, sem `tenant_id`, entao o portao de papel e o unico controle —
// ver o docstring de `routers/admin_reference_data.py`.
// ---------------------------------------------------------------------------

const REF_KEY = ["admin", "reference-data"] as const;

export function useReferenceDataSources() {
  return useQuery({
    queryKey: [...REF_KEY, "sources"],
    queryFn: listReferenceDataSources,
    // Live-ish: active version / row counts move when someone promotes.
    refetchInterval: 60_000,
  });
}

export function useReferenceDataRecords(
  tableName: string | undefined,
  params: { limit?: number; offset?: number; q?: string; column?: string } = {},
) {
  return useQuery({
    queryKey: [
      ...REF_KEY, "records", tableName, params.limit, params.offset,
      params.q ?? "", params.column ?? "",
    ],
    queryFn: () => listReferenceDataRecords(tableName!, params),
    enabled: !!tableName,
    placeholderData: (prev) => prev, // keep rows visible while paging
  });
}

export function useReferenceDataSchema(tableName: string | undefined) {
  return useQuery({
    queryKey: [...REF_KEY, "schema", tableName],
    queryFn: () => getReferenceDataSchema(tableName!),
    enabled: !!tableName,
    staleTime: 5 * 60_000, // schema is stable
  });
}

export function useReferenceDataHistory(
  tableName: string | undefined,
  params: { operation_type?: string; status?: string; limit?: number; offset?: number } = {},
) {
  return useQuery({
    queryKey: [
      ...REF_KEY,
      "history",
      tableName,
      params.operation_type,
      params.status,
      params.limit,
      params.offset,
    ],
    queryFn: () => getReferenceDataHistory(tableName!, params),
    enabled: !!tableName,
  });
}

export function useReferenceDataVersions(tableName: string | undefined) {
  return useQuery({
    queryKey: [...REF_KEY, "versions", tableName],
    queryFn: () => listReferenceDataVersions(tableName!),
    enabled: !!tableName,
  });
}

export function useUploadReferenceDataFile(tableName: string) {
  return useMutation({
    mutationFn: (file: File) => uploadReferenceDataFile(tableName, file),
  });
}

export function useIngestReferenceData(tableName: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { object_key: string; filename: string; expected_sha256?: string }) =>
      ingestReferenceData(tableName, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...REF_KEY, "history", tableName] });
      qc.invalidateQueries({ queryKey: [...REF_KEY, "versions", tableName] });
    },
  });
}

/**
 * Poll one ingestion run until it leaves the running/queued state. `runId`
 * of undefined disables the query (nothing to poll yet).
 */
export function useReferenceDataIngestStatus(
  tableName: string,
  runId: string | undefined,
) {
  return useQuery({
    queryKey: [...REF_KEY, "ingest-status", tableName, runId],
    queryFn: () => getReferenceDataIngestStatus(tableName, runId!),
    enabled: !!runId,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "running" || s === "queued" ? 2_000 : false;
    },
  });
}

function useRefLifecycleInvalidation(tableName: string) {
  const qc = useQueryClient();
  return () => {
    // A promote/rollback flips which rows are active — refresh everything.
    qc.invalidateQueries({ queryKey: [...REF_KEY, "sources"] });
    // O resumo dos agentes carrega a release ativa do tipo: promover ou voltar
    // uma tabela de tipo muda o que ele mostra.
    qc.invalidateQueries({ queryKey: [...REF_KEY, "agents"] });
    qc.invalidateQueries({ queryKey: [...REF_KEY, "records", tableName] });
    qc.invalidateQueries({ queryKey: [...REF_KEY, "versions", tableName] });
    qc.invalidateQueries({ queryKey: [...REF_KEY, "history", tableName] });
  };
}

export function usePromoteReferenceData(tableName: string) {
  const invalidate = useRefLifecycleInvalidation(tableName);
  return useMutation({
    mutationFn: (ingestRunId: string) => promoteReferenceData(tableName, ingestRunId),
    onSuccess: invalidate,
  });
}

export function useRollbackReferenceData(tableName: string) {
  const invalidate = useRefLifecycleInvalidation(tableName);
  return useMutation({
    mutationFn: (ingestRunId: string) => rollbackReferenceData(tableName, ingestRunId),
    onSuccess: invalidate,
  });
}

/**
 * The earlier versions of ONE row, for the per-row rollback dialog (S-046).
 * Enabled only once a row is picked — the dialog mounts before that.
 */
export function useRecordVersions(tableName: string, recordId: string | null) {
  return useQuery({
    queryKey: [...REF_KEY, "recordVersions", tableName, recordId],
    queryFn: () => listRecordVersions(tableName, recordId as string),
    enabled: Boolean(tableName && recordId),
  });
}

/**
 * Revert ONE row to another version of its natural key. Invalidates the same
 * lifecycle queries as a table-wide rollback: the active set changed, and the
 * operation is recorded as a `rollback` run in the audit log.
 */
export function useRollbackReferenceDataRecord(tableName: string) {
  const invalidate = useRefLifecycleInvalidation(tableName);
  return useMutation({
    mutationFn: ({ recordId, targetRowId }: { recordId: string; targetRowId: string }) =>
      rollbackReferenceDataRecord(tableName, recordId, targetRowId),
    onSuccess: invalidate,
  });
}

/**
 * Stage a batch of row-level create/update edits as a new `edit` version.
 * Invalidates the lifecycle queries so the fresh draft shows up in Versions &
 * audit (where it is promoted — same flow as an ingest).
 */
export function useStageReferenceDataEdits(tableName: string) {
  const invalidate = useRefLifecycleInvalidation(tableName);
  return useMutation({
    mutationFn: (operations: ReferenceDataEditOperation[]) =>
      stageReferenceDataEdits(tableName, operations),
    onSuccess: invalidate,
  });
}

/** `enabled: false` para quem só precisa do resumo em certos casos (fonte de tipo). */
export function useReferenceDataAgents(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...REF_KEY, "agents"],
    queryFn: listReferenceDataAgents,
    refetchInterval: 60_000,
    enabled: options?.enabled ?? true,
  });
}

export function useConfigPackages(key: string | undefined) {
  return useQuery({
    queryKey: [...REF_KEY, "packages", key],
    queryFn: () => listConfigPackages(key!),
    enabled: !!key,
    refetchInterval: (query) =>
      (query.state.data?.items.some((pkg) => pkg.state === "running") ? 2_000 : false),
  });
}

/**
 * Polling do pacote até sair de running. `packageId` undefined desliga a consulta.
 * Com `tables`, quando o pacote chega a um estado final (staged, failed ou active) as
 * versões e a auditoria dessas tabelas são refeitas: a run nova já existe nelas.
 * Só um observador por pacote deve passar `tables` (a página do tipo); os demais leem a
 * mesma consulta do cache sem invalidar de novo.
 */
export function useConfigPackage(
  key: string,
  packageId: string | undefined,
  tables?: readonly string[],
) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: [...REF_KEY, "package", key, packageId],
    queryFn: () => getConfigPackage(key, packageId!),
    enabled: !!packageId,
    refetchInterval: (q) => (q.state.data?.state === "running" ? 2_000 : false),
  });
  const state = query.data?.state;
  const previous = useRef<{ packageId?: string; state?: string }>({});
  useEffect(() => {
    const before = previous.current;
    previous.current = { packageId, state };
    // Vale também undefined -> staged: a carga pode terminar antes da primeira consulta.
    const settled =
      before.packageId === packageId &&
      before.state !== state &&
      !!state &&
      state !== "running";
    if (!settled || !tables) return;
    for (const table of tables) {
      qc.invalidateQueries({ queryKey: [...REF_KEY, "versions", table] });
      qc.invalidateQueries({ queryKey: [...REF_KEY, "history", table] });
    }
  }, [packageId, state, tables, qc]);
  return query;
}

export function useUploadConfigTypeFile(key: string) {
  return useMutation({ mutationFn: (file: File) => uploadConfigTypeFile(key, file) });
}

export function useIngestConfigType(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      object_key: string;
      filename?: string;
      expected_sha256?: string;
      options?: Record<string, string>;
    }) => ingestConfigType(key, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...REF_KEY, "packages", key] }),
  });
}

function useConfigPackageInvalidation(key: string, tables: readonly string[]) {
  const qc = useQueryClient();
  return () => {
    // Promote/rollback do pacote troca as linhas ativas de todas as tabelas do tipo.
    qc.invalidateQueries({ queryKey: [...REF_KEY, "sources"] });
    qc.invalidateQueries({ queryKey: [...REF_KEY, "agents"] });
    qc.invalidateQueries({ queryKey: [...REF_KEY, "packages", key] });
    qc.invalidateQueries({ queryKey: [...REF_KEY, "package", key] });
    for (const table of tables) {
      qc.invalidateQueries({ queryKey: [...REF_KEY, "records", table] });
      qc.invalidateQueries({ queryKey: [...REF_KEY, "versions", table] });
      qc.invalidateQueries({ queryKey: [...REF_KEY, "history", table] });
    }
  };
}

export function usePromoteConfigPackage(key: string, tables: readonly string[]) {
  const invalidate = useConfigPackageInvalidation(key, tables);
  return useMutation({
    mutationFn: (packageId: string) => promoteConfigPackage(key, packageId),
    onSuccess: invalidate,
  });
}

export function useRollbackConfigPackage(key: string, tables: readonly string[]) {
  const invalidate = useConfigPackageInvalidation(key, tables);
  return useMutation({
    mutationFn: (packageId: string) => rollbackConfigPackage(key, packageId),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Chat History
// ---------------------------------------------------------------------------

export function useChatHistory(
  projectId: string | undefined,
  agentId: string | undefined,
  executionId?: string,
) {
  return useQuery({
    queryKey: ["chatHistory", projectId, agentId, executionId],
    queryFn: () => getChatHistory(projectId!, agentId!, executionId),
    enabled: !!projectId && !!agentId,
  });
}

// ---------------------------------------------------------------------------
// Project members — atribuição usuário↔projeto (SEC.4 no backend, S-073 na tela)
// ---------------------------------------------------------------------------

export function useProjectMembers(projectId: string | undefined) {
  return useQuery({
    queryKey: ["project", projectId, "members"],
    queryFn: () => getProjectMembers(projectId!),
    enabled: !!projectId,
  });
}

/**
 * O picker de "adicionar membro".
 *
 * Sem `enabled` próprio: a rota exige `manager` **e** alcance ao projeto, e é
 * exatamente o que a aba de Settings já exige para montar (`PROJECT_TABS`).
 * Quem chega aqui pode chamar.
 */
/**
 * Candidatos a membro do projeto.
 *
 * `enabled` é do chamador porque o custo é dele: o `_LIST_ASSIGNABLE` devolve
 * **todo** usuário ativo do tenant, ordenado, sem `LIMIT` e sem busca, e quem
 * consome é um `<Select>` dentro de um diálogo que a maioria das visitas à tela
 * de configuração não abre. O padrão é o mesmo do `useAdminUsers`.
 */
export function useAssignableUsers(
  projectId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ["project", projectId, "assignable-users"],
    queryFn: () => getAssignableUsers(projectId!),
    enabled: !!projectId && options.enabled !== false,
  });
}

export function useAddProjectMember(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => addProjectMember(projectId!, userId),
    // Só `["project", id]`: quem entrou sai da lista de candidatos do picker.
    // `["projects"]` **não** entra aqui, ao contrário da revogação — a lista de
    // quem chama não muda por ele ter dado acesso a outra pessoa, e o payload de
    // `/api/projects` não carrega contagem de membros. Invalidar seria uma
    // re-busca que nunca produz linha diferente.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });
}

/**
 * Revogar acesso, e as **duas** chaves que isso mexe.
 *
 * `["project", id]` é o óbvio: a lista de membros e o picker vivem sob ele.
 *
 * `["projects"]` é o que faltava, e é o que quebrava a promessa do diálogo.
 * Quem revoga a própria participação deixa de alcançar o projeto na hora — o
 * manager **não** é isento de atribuição, só o admin é (`_MEMBERSHIP_EXEMPT_FROM`
 * em `project_access.py`). Sem invalidar a lista, `/projects` continua exibindo
 * um projeto que agora responde `404`, e continua exibindo por 30s inteiros,
 * porque é o `staleTime` da query. O bug some sozinho — o que é pior do que não
 * sumir, porque quem for investigar não o encontra.
 *
 * Sair da tela que passou a dar 404 **não** é feito aqui: `useApi.ts` é camada de
 * dados e não importa o router em lugar nenhum. Quem navega é a tela, que já sabe
 * quem é `me` — ver `MembersCard.handleRevoke`.
 */
export function useRevokeProjectMember(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => revokeProjectMember(projectId!, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Runs do projeto (#867)
// ---------------------------------------------------------------------------

/** Chave de cache da listagem, com **todo** filtro dentro.
 *
 * Exportada para ter teste próprio. A lição é do `useProjectCosts`: quando
 * `includeDaily` ficou de fora da chave, a resposta enxuta da Overview era
 * servida para a tela de custo, que desenhava empty state para um projeto com
 * histórico. Aqui o mesmo defeito apareceria como a página 2 servida no lugar
 * da 1 — a tela responde, só que com a lista errada.
 */
export function projectRunsQueryKey(
  projectId: string | undefined,
  options: GetProjectRunsOptions,
) {
  return [
    "projectRuns",
    projectId,
    options.status ?? null,
    options.agentType ?? null,
    options.q ?? null,
    options.dateFrom ?? null,
    options.dateTo ?? null,
    // Dentro da chave pela mesma razão dos outros: a tela de custo pede só as
    // runs com custo, e sem isto dividiria o cache com a lista inteira da tela
    // de Runs.
    options.hasCost ?? false,
    options.page ?? 1,
    options.limit ?? null,
  ] as const;
}

export function useProjectRuns(
  projectId: string | undefined,
  options: GetProjectRunsOptions = {},
) {
  return useQuery({
    queryKey: projectRunsQueryKey(projectId, options),
    queryFn: () => getProjectRuns(projectId!, options),
    enabled: !!projectId,
  });
}

export function useProjectRunStats(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projectRunStats", projectId],
    queryFn: () => getProjectRunStats(projectId!),
    enabled: !!projectId,
  });
}
